import { FC, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useLanguage } from "src/hooks/useLanguage";
import { toArabicIndicDigitsInString } from "src/utils/numberFormat";
import { mirrorChartMargin, rtlChartProps } from "src/components/charts/chartRtl";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CheckCircle2,
  Check,
  ChevronDown,
  Package,
  Store,
  TriangleAlert,
  Search,
} from "lucide-react";
import {
  OMNICHANNEL_TAB_ITEMS,
  computeChangePercent,
  generateOmnichannelCustomers,
  generateOmnichannelOrders,
  generateOmnichannelProducts,
  getChannelDistribution,
  getCltvBuckets,
  getCustomerSegmentation,
  getFinancialSummary,
  getMonthlyFinancials,
  getOverviewKpis,
  getOverviewSalesTrend,
  getPreviousDateRange,
  getTaxBreakdown,
  paginateRows,
  type OmnichannelCustomer,
  type OmnichannelOrder,
  type OmnichannelProduct,
  type OmnichannelSource,
  type OmnichannelStatus,
  type OmnichannelTabId,
} from "./OmnichannelAnalyticsTabsDashboard.data";
import { getDashboardKpiLucideIconComponent } from "src/utils/dashboardKpiLucideIcon";
import CustomChartTooltip from "../../../components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  DashboardKpiCard,
  DefinitionTooltip,
  compareDashboardValues,
  dashboardDataTable as dt,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DashboardSectionHeader,
  TablePagePagination,
  toggleTableSort,
  type DefinitionTooltipInfo,
  type SortDirection,
} from "src/components/common";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import { CHART_COLOR_PALETTE } from "src/components/charts/chartPalette";
import OmnichannelExecutiveSummary from "./OmnichannelExecutiveSummary";
import "./OmnichannelAnalyticsTabsDashboard.scss";

const PIE_COLORS = CHART_COLOR_PALETTE;
const DAILY_LINE_COLORS = {
  zid: CHART_COLOR_PALETTE[0],
  odoo: CHART_COLOR_PALETTE[3],
};
const PAGE_SIZE = 10;

/** Set once per render from the top-level dashboard component; every
 * formatter below reads it so digits localize without threading `isRTL`
 * through every call site individually. */
let isArabicRender = false;

const localizeDigits = (text: string) =>
  isArabicRender ? toArabicIndicDigitsInString(text) : text;

const formatTrendDate = (isoDate: string) => {
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return parsed.toLocaleDateString(isArabicRender ? "ar-SA" : "en-US", {
    month: "short",
    day: "numeric",
  });
};

const formatMonthLabel = (isoMonth: string) => {
  const parsed = new Date(`${isoMonth}-01T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return isoMonth;
  }
  return parsed.toLocaleDateString(isArabicRender ? "ar-SA" : "en-US", {
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value: number) =>
  localizeDigits(`SAR ${Math.round(value).toLocaleString()}`);

const formatCompactCurrency = (value: number) =>
  localizeDigits(value >= 1000 ? `SAR ${(value / 1000).toFixed(1)}k` : `SAR ${value}`);

const formatCurrencyTooltipValue = (value: number) => formatCurrency(value);
const formatNumberTooltipValue = (value: number) => localizeDigits(String(value));

const tableTotalPages = (totalRows: number) =>
  Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

/** Display-only Arabic strings for this dashboard's fixed English UI text.
 * Underlying data (`.label`/status/type values used for lookups, equality
 * checks, or sorting) is left untouched — only what's shown in JSX is mapped. */
const OMNICHANNEL_AR_STRINGS: Record<string, string> = {
  Overview: "نظرة عامة",
  Sales: "المبيعات",
  Financials: "الماليات",
  "Dashboard Overview": "نظرة عامة على لوحة المعلومات",
  "Unified view across Zid and Odoo channels": "عرض موحّد عبر قنوات Zid, Odoo",
  Revenue: "الإيرادات",
  Orders: "الطلبات",
  Customers: "العملاء",
  "Avg. Order": "متوسط الطلب",
  Inventory: "المخزون",
  "Avg. CLTV": "متوسط القيمة الدائمة للعميل",
  "Total Products": "إجمالي المنتجات",
  "In Stock": "متوفر",
  "Low Stock": "مخزون منخفض",
  "Inventory Value": "قيمة المخزون",
  "Total Customers": "إجمالي العملاء",
  "New Customers": "عملاء جدد",
  "Avg. Order Value": "متوسط قيمة الطلب",
  "Net Profit": "صافي الربح",
  "Total Taxes": "إجمالي الضرائب",
  Refunds: "المبالغ المستردة",
  "Out of Stock": "نفد المخزون",
  "vs last period": "مقارنة بالفترة السابقة",
  "vs previous period": "مقارنة بالفترة السابقة",
  "new this week": "جديد هذا الأسبوع",
  "low stock": "مخزون منخفض",
  "available": "متوفر",
  "margin": "هامش",
  "Sales Trend": "اتجاه المبيعات",
  Zid: "Zid",
  Odoo: "Odoo",
  "Channel Distribution": "توزيع القنوات",
  "Online Store": "المتجر الإلكتروني",
  "Physical Store": "المتجر الفعلي",
  "Top Products": "أفضل المنتجات",
  Product: "المنتج",
  Stock: "المخزون",
  Price: "السعر",
  "Customer Breakdown": "تحليل العملاء",
  New: "جديد",
  Returning: "متكرر",
  "Financial Summary": "الملخص المالي",
  "Gross Profit": "إجمالي الربح",
  Taxes: "الضرائب",
  Expenses: "المصروفات",
  "Sales Overview": "نظرة عامة على المبيعات",
  "Track revenue and order performance across Zid and Odoo channels.":
    "تتبّع أداء الإيرادات والطلبات عبر قنوات Zid وOdoo.",
  "Sales by Channel": "المبيعات حسب القناة",
  "Order revenue split across omnichannel storefronts.":
    "توزيع إيرادات الطلبات عبر متاجر القنوات المتعددة.",
  "Search orders...": "البحث في الطلبات...",
  "All Sources": "جميع المصادر",
  "All Statuses": "جميع الحالات",
  Completed: "مكتمل",
  Pending: "قيد الانتظار",
  Cancelled: "ملغى",
  Order: "الطلب",
  Customer: "العميل",
  Refunded: "مسترد",
  Source: "المصدر",
  Channel: "القناة",
  Total: "الإجمالي",
  Status: "الحالة",
  Date: "التاريخ",
  "Inventory Overview": "نظرة عامة على المخزون",
  "Compare stock, availability, and inventory value across both sources.":
    "قارن المخزون والتوفر وقيمة المخزون عبر المصدرين.",
  "Unified catalog count": "إجمالي عدد المنتجات الموحّد",
  "Needs reorder attention": "يحتاج إلى إعادة الطلب",
  "Stock Comparison": "مقارنة المخزون",
  "Comparative stock visibility across both connected sources.":
    "رؤية مقارنة للمخزون عبر المصدرين المتصلين.",
  Products: "المنتجات",
  "Search products...": "البحث في المنتجات...",
  "All status": "جميع الحالات",
  "In stock": "متوفر",
  "Low stock": "مخزون منخفض",
  "Out of stock": "نفد المخزون",
  SKU: "رمز المنتج",
  Category: "الفئة",
  Reorder: "إعادة الطلب",
  "Customers Overview": "نظرة عامة على العملاء",
  "Review customer growth, loyalty, and top buyers across channels.":
    "راجع نمو العملاء وولاءهم وأبرز المشترين عبر القنوات.",
  "Active Zid and Odoo buyers": "المشترون النشطون عبر Zid وOdoo",
  "Recent acquisitions": "عمليات اكتساب حديثة",
  "Customer lifetime value": "القيمة الدائمة للعميل",
  "Average basket size": "متوسط حجم السلة",
  "CLTV Distribution": "توزيع القيمة الدائمة للعميل",
  "Customer Segmentation": "تصنيف العملاء",
  "Top Customers": "أفضل العملاء",
  "Search customers...": "البحث في العملاء...",
  "All types": "جميع الأنواع",
  Name: "الاسم",
  Type: "النوع",
  "Total Spent": "إجمالي الإنفاق",
  CLTV: "القيمة الدائمة للعميل",
  "Last Order": "آخر طلب",
  "Financials Overview": "نظرة عامة على الماليات",
  "Analyze omnichannel revenue, profit, taxes, and refund performance.":
    "حلّل أداء الإيرادات والأرباح والضرائب والمبالغ المستردة عبر القنوات.",
  "Monthly P&L": "الأرباح والخسائر الشهرية",
  Profit: "الربح",
  "Revenue by Channel": "الإيرادات حسب القناة",
  "Tax Breakdown": "تفاصيل الضرائب",
  "Sales Tax": "ضريبة المبيعات",
  VAT: "ضريبة القيمة المضافة",
  Other: "أخرى",
  "Financial Breakdown": "التفاصيل المالية",
  "Gross Revenue": "إجمالي الإيرادات",
  "Cost of Goods Sold": "تكلفة البضاعة المباعة",
  "Operating Expenses": "المصروفات التشغيلية",
  "Total spent (high → low)": "إجمالي الإنفاق (من الأعلى إلى الأدنى)",
  "Total spent (low → high)": "إجمالي الإنفاق (من الأدنى إلى الأعلى)",
  "CLTV (high → low)": "القيمة الدائمة للعميل (من الأعلى إلى الأدنى)",
  "CLTV (low → high)": "القيمة الدائمة للعميل (من الأدنى إلى الأعلى)",
  "Orders (high → low)": "الطلبات (من الأعلى إلى الأدنى)",
  "Orders (low → high)": "الطلبات (من الأدنى إلى الأعلى)",
  "Name (A–Z)": "الاسم (أ–ي)",
  "Name (Z–A)": "الاسم (ي–أ)",
  "Type (A–Z)": "النوع (أ–ي)",
  "Type (Z–A)": "النوع (ي–أ)",
  "Last order (newest)": "آخر طلب (الأحدث)",
  "Last order (oldest)": "آخر طلب (الأقدم)",
  "Shows the list of products with their current stock levels and average price.":
    "يعرض قائمة المنتجات مع مستويات المخزون الحالية ومتوسط السعر.",
  Formula: "المعادلة",
  "Total revenue from completed orders across connected channels in the selected range.":
    "إجمالي الإيرادات من الطلبات المكتملة عبر القنوات المتصلة خلال الفترة المحددة.",
  "Sum of completed order totals": "مجموع إجماليات الطلبات المكتملة",
  "Completed orders across Zid and Odoo in the selected period.":
    "الطلبات المكتملة عبر Zid وOdoo خلال الفترة المحددة.",
  "Completed orders": "الطلبات المكتملة",
  "Customers with at least one completed order in the combined dataset.":
    "العملاء الذين لديهم طلب مكتمل واحد على الأقل في مجموعة البيانات المجمّعة.",
  "Distinct customers": "عملاء مميزون",
  "Average order value across completed orders.": "متوسط قيمة الطلب عبر الطلبات المكتملة.",
  "Total revenue ÷ Completed orders": "إجمالي الإيرادات ÷ الطلبات المكتملة",
  "Estimated value of on-hand inventory using unit cost.":
    "القيمة المقدّرة للمخزون المتوفر باستخدام تكلفة الوحدة.",
  "Sum of (stock × cost) per SKU": "مجموع (المخزون × التكلفة) لكل رمز منتج",
  "Average predicted customer lifetime value in your cohort.":
    "متوسط القيمة الدائمة المتوقعة للعميل ضمن مجموعتك.",
  "Mean of customer CLTV estimates": "متوسط تقديرات القيمة الدائمة للعملاء",
  "Products in your connected catalogs.": "المنتجات في كتالوجاتك المتصلة.",
  "Count of SKUs": "عدد رموز المنتجات",
  "Products currently above their reorder threshold.":
    "المنتجات التي تتجاوز حاليًا حد إعادة الطلب.",
  "SKUs with status in stock": "رموز المنتجات المتوفرة",
  "Products at or below reorder point and need replenishment.":
    "المنتجات عند حد إعادة الطلب أو أقل وتحتاج إلى تجديد المخزون.",
  "SKUs flagged low stock": "رموز المنتجات ذات المخزون المنخفض",
  "Monetary value of inventory on hand.": "القيمة النقدية للمخزون المتوفر.",
  "Sum of stock × unit cost": "مجموع المخزون × تكلفة الوحدة",
  "All customers in the current analytics sample.": "جميع العملاء في عينة التحليلات الحالية.",
  "Distinct customer records": "سجلات عملاء مميزة",
  "Customers classified as new in the selected window.":
    "العملاء المصنّفون كجدد خلال الفترة المحددة.",
  "Customers with first purchase in range": "العملاء ذوو أول عملية شراء خلال الفترة",
  "Average basket size for customers in this view.": "متوسط حجم السلة للعملاء في هذا العرض.",
  "Mean of order totals": "متوسط إجماليات الطلبات",
  "Revenue less cost of goods, taxes, expenses, and refunds.":
    "الإيرادات ناقص تكلفة البضاعة والضرائب والمصروفات والمبالغ المستردة.",
  "Revenue − COGS − Taxes − Expenses − Refunds":
    "الإيرادات − تكلفة البضاعة − الضرائب − المصروفات − المبالغ المستردة",
  "Tax collected on completed orders in the period.":
    "الضرائب المحصّلة على الطلبات المكتملة خلال الفترة.",
  "Sum of order tax lines": "مجموع بنود ضرائب الطلبات",
  "Value of refunded orders in the period.": "قيمة الطلبات المستردة خلال الفترة.",
  "Sum of refund totals": "مجموع إجماليات المبالغ المستردة",
  "SKUs with zero on-hand availability.": "رموز المنتجات ذات التوفر الصفري.",
  "Count of out-of-stock products": "عدد المنتجات نافدة المخزون",
};

const makeTr = (isArabic: boolean) => (text: string): string =>
  isArabic ? OMNICHANNEL_AR_STRINGS[text] ?? text : text;

type InventorySortKey =
  | "name"
  | "sku"
  | "category"
  | "zidStock"
  | "odooStock"
  | "totalStock"
  | "reorderPoint"
  | "status";
type CustomerSortKey = "name" | "type" | "totalOrders" | "totalSpent" | "cltv" | "lastOrder";

const TOP_PRODUCTS_TOOLTIP: DefinitionTooltipInfo = {
  description:
    "Shows the list of products with their current stock levels and average price.",
};

const parseKeyDirection = <K extends string>(value: string): { key: K; direction: SortDirection } => {
  const last = value.lastIndexOf("_");
  return {
    key: value.slice(0, last) as K,
    direction: value.slice(last + 1) as SortDirection,
  };
};

const OMNICHANNEL_CUSTOMER_SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "totalSpent_desc", label: "Total spent (high → low)" },
  { value: "totalSpent_asc", label: "Total spent (low → high)" },
  { value: "cltv_desc", label: "CLTV (high → low)" },
  { value: "cltv_asc", label: "CLTV (low → high)" },
  { value: "totalOrders_desc", label: "Orders (high → low)" },
  { value: "totalOrders_asc", label: "Orders (low → high)" },
  { value: "name_asc", label: "Name (A–Z)" },
  { value: "name_desc", label: "Name (Z–A)" },
  { value: "type_asc", label: "Type (A–Z)" },
  { value: "type_desc", label: "Type (Z–A)" },
  { value: "lastOrder_desc", label: "Last order (newest)" },
  { value: "lastOrder_asc", label: "Last order (oldest)" },
];

const OMNICHANNEL_METRIC_TOOLTIPS: Record<string, DefinitionTooltipInfo> = {
  Revenue: {
    description: "Total revenue from completed orders across connected channels in the selected range.",
    formula: "Sum of completed order totals",
  },
  Orders: {
    description: "Completed orders across Zid and Odoo in the selected period.",
    formula: "Completed orders",
  },
  Customers: {
    description: "Customers with at least one completed order in the combined dataset.",
    formula: "Distinct customers",
  },
  "Avg. Order": {
    description: "Average order value across completed orders.",
    formula: "Total revenue ÷ Completed orders",
  },
  Inventory: {
    description: "Estimated value of on-hand inventory using unit cost.",
    formula: "Sum of (stock × cost) per SKU",
  },
  "Avg. CLTV": {
    description: "Average predicted customer lifetime value in your cohort.",
    formula: "Mean of customer CLTV estimates",
  },
  "Total Products": {
    description: "Products in your connected catalogs.",
    formula: "Count of SKUs",
  },
  "In Stock": {
    description: "Products currently above their reorder threshold.",
    formula: "SKUs with status in stock",
  },
  "Low Stock": {
    description: "Products at or below reorder point and need replenishment.",
    formula: "SKUs flagged low stock",
  },
  "Inventory Value": {
    description: "Monetary value of inventory on hand.",
    formula: "Sum of stock × unit cost",
  },
  "Total Customers": {
    description: "All customers in the current analytics sample.",
    formula: "Distinct customer records",
  },
  "New Customers": {
    description: "Customers classified as new in the selected window.",
    formula: "Customers with first purchase in range",
  },
  "Avg. Order Value": {
    description: "Average basket size for customers in this view.",
    formula: "Mean of order totals",
  },
  "Net Profit": {
    description: "Revenue less cost of goods, taxes, expenses, and refunds.",
    formula: "Revenue − COGS − Taxes − Expenses − Refunds",
  },
  "Total Taxes": {
    description: "Tax collected on completed orders in the period.",
    formula: "Sum of order tax lines",
  },
  Refunds: {
    description: "Value of refunded orders in the period.",
    formula: "Sum of refund totals",
  },
  "Out of Stock": {
    description: "SKUs with zero on-hand availability.",
    formula: "Count of out-of-stock products",
  },
};

const MetricCard: FC<{
  label: string;
  value: string;
  tone?: "positive" | "negative" | "neutral";
  icon?: FC<{ className?: string }>;
  changePercent?: number | null;
  comparisonLabel?: string;
  statusHint?: string;
  hideTrend?: boolean;
}> = ({
  label,
  value,
  tone = "neutral",
  icon: Icon,
  changePercent,
  comparisonLabel = "vs last period",
  statusHint,
  hideTrend = false,
}) => {
  const { isRTL } = useLanguage();
  const tr = makeTr(isRTL);
  const baseTooltip = OMNICHANNEL_METRIC_TOOLTIPS[label];
  const tooltip = baseTooltip
    ? {
        description: tr(baseTooltip.description),
        formula: baseTooltip.formula ? tr(baseTooltip.formula) : undefined,
      }
    : undefined;

  return (
    <DashboardKpiCard
      title={tr(label)}
      value={value}
      metricLabelForTooltip={label}
      tooltip={tooltip}
      formulaTitle={tr("Formula")}
      icon={
        Icon ? (
          <Icon className="sales-product-analytics-dashboard__metric-icon-svg" />
        ) : undefined
      }
      changePercent={changePercent}
      comparisonLabel={tr(comparisonLabel)}
      description={statusHint ? tr(statusHint) : undefined}
      tone={tone}
      hideNoTrendFallback={hideTrend}
    />
  );
};

const SourceBadge: FC<{ source: OmnichannelSource }> = ({ source }) => (
  <span
    className={`sales-product-analytics-dashboard__source-badge sales-product-analytics-dashboard__source-badge--${source}`}
  >
    <Store size={12} />
    <span>{source === "zid" ? "Zid" : "Odoo"}</span>
  </span>
);

const SelectMenu: FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (_value: string) => void;
  className?: string;
}> = ({ value, options, onChange, className = "" }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(option => option.value === value) ?? options[0];

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  return (
    <div
      ref={rootRef}
      className={`sales-product-analytics-dashboard__select-menu ${className}`.trim()}
    >
      <button
        type="button"
        className={`sales-product-analytics-dashboard__select-trigger ${open ? "sales-product-analytics-dashboard__select-trigger--open" : ""}`}
        onClick={() => setOpen(current => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selected.label}</span>
        <ChevronDown className="sales-product-analytics-dashboard__select-trigger-icon" />
      </button>
      {open ? (
        <div className="sales-product-analytics-dashboard__select-dropdown" role="listbox">
          {options.map(option => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`sales-product-analytics-dashboard__select-option ${active ? "sales-product-analytics-dashboard__select-option--active" : ""}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span className="sales-product-analytics-dashboard__select-option-check">
                  {active ? <Check size={16} /> : null}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const OverviewSection: FC<{
  orders: OmnichannelOrder[];
  customers: OmnichannelCustomer[];
  products: OmnichannelProduct[];
  previousOrders: OmnichannelOrder[];
  previousCustomers: OmnichannelCustomer[];
  dateRange: { startDate: string; endDate: string };
  setDateRange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ orders, customers, products, previousOrders, previousCustomers, dateRange, setDateRange }) => {
  const { isRTL } = useLanguage();
  const tr = makeTr(isRTL);
  const trendData = useMemo(() => getOverviewSalesTrend(dateRange), [dateRange]);
  const channelData = useMemo(() => getChannelDistribution(orders), [orders]);
  const summary = useMemo(() => getFinancialSummary(orders), [orders]);
  const kpis = useMemo(
    () => getOverviewKpis(orders, customers, products),
    [orders, customers, products]
  );
  const previousKpis = useMemo(
    () => getOverviewKpis(previousOrders, previousCustomers, products),
    [previousOrders, previousCustomers, products]
  );
  const topProducts = useMemo(() => products.slice(0, 5), [products]);
  const segmentation = useMemo(() => getCustomerSegmentation(customers), [customers]);
  const newCustomers = segmentation.find(item => item.label === "New")?.value ?? 0;
  const returningCustomers =
    segmentation.find(item => item.label === "Returning")?.value ?? 0;
  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title={tr("Dashboard Overview")}
        subtitle={tr("Unified view across Zid and Odoo channels")}
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={setDateRange}
          />
        )}
      />

      <OmnichannelExecutiveSummary
        tab="overview"
        dateRange={dateRange}
        kpis={kpis}
        previousKpis={previousKpis}
        channelData={channelData}
        segmentation={segmentation}
        products={products}
      />

      <div className="sales-product-analytics-dashboard__metrics sales-product-analytics-dashboard__metrics--overview">
        <MetricCard
          label="Revenue"
          value={formatCurrency(kpis.totalRevenue)}
          changePercent={computeChangePercent(kpis.totalRevenue, previousKpis.totalRevenue)}
          tone={kpis.totalRevenue >= previousKpis.totalRevenue ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Revenue")}
        />
        <MetricCard
          label="Orders"
          value={localizeDigits(String(kpis.totalOrders))}
          changePercent={computeChangePercent(kpis.totalOrders, previousKpis.totalOrders)}
          tone={kpis.totalOrders >= previousKpis.totalOrders ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Orders")}
        />
        <MetricCard
          label="Customers"
          value={localizeDigits(String(kpis.activeCustomers))}
          changePercent={computeChangePercent(kpis.activeCustomers, previousKpis.activeCustomers)}
          statusHint={
            isRTL
              ? `${newCustomers} ${tr("new this week")}`
              : `${newCustomers} new this week`
          }
          tone={kpis.activeCustomers >= previousKpis.activeCustomers ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Customers")}
        />
        <MetricCard
          label="Avg. Order"
          value={formatCurrency(kpis.avgOrderValue)}
          changePercent={computeChangePercent(kpis.avgOrderValue, previousKpis.avgOrderValue)}
          tone={kpis.avgOrderValue >= previousKpis.avgOrderValue ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Avg. Order")}
        />
        <MetricCard
          label="Inventory"
          value={formatCompactCurrency(kpis.inventoryValue)}
          statusHint={
            isRTL
              ? `${products.filter(product => product.status === "low_stock").length} ${tr("low stock")}`
              : `${products.filter(product => product.status === "low_stock").length} low stock`
          }
          tone="neutral"
          icon={getDashboardKpiLucideIconComponent("Inventory")}
          hideTrend
        />
        <MetricCard
          label="Avg. CLTV"
          value={formatCurrency(kpis.avgCltv)}
          changePercent={computeChangePercent(kpis.avgCltv, previousKpis.avgCltv)}
          tone={kpis.avgCltv >= previousKpis.avgCltv ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Avg. CLTV")}
        />
      </div>

      <div className="sales-product-analytics-dashboard__grid">
        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">
            {tr("Sales Trend")}
          </h3>
          <div className="sales-product-analytics-dashboard__chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={mirrorChartMargin({ left: 0, right: 0, top: 0, bottom: 0 }, isRTL)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval={6}
                  reversed={isRTL}
                  tickFormatter={value => localizeDigits(formatTrendDate(value))}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  orientation={rtlChartProps(isRTL).yAxisOrientation}
                  tickFormatter={value => localizeDigits(`SAR ${Math.round(value / 1000)}k`)}
                />
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                      formatName={tr}
                      formatLabel={value => localizeDigits(formatTrendDate(value))}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
                <Line
                  type="monotone"
                  dataKey="zid"
                  stroke={DAILY_LINE_COLORS.zid}
                  strokeWidth={2.5}
                  dot={false}
                  name="Zid"
                />
                <Line
                  type="monotone"
                  dataKey="odoo"
                  stroke={DAILY_LINE_COLORS.odoo}
                  strokeWidth={2.5}
                  dot={false}
                  name="Odoo"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__chart-legend">
            <span className="sales-product-analytics-dashboard__chart-legend-item">
              <span
                className="sales-product-analytics-dashboard__chart-legend-swatch"
                style={{ color: DAILY_LINE_COLORS.zid }}
              />
              <span>Zid</span>
            </span>
            <span className="sales-product-analytics-dashboard__chart-legend-item">
              <span
                className="sales-product-analytics-dashboard__chart-legend-swatch"
                style={{ color: DAILY_LINE_COLORS.odoo }}
              />
              <span>Odoo</span>
            </span>
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">{tr("Channel Distribution")}</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={channelData} dataKey="value" nameKey="label" innerRadius={48} outerRadius={82} paddingAngle={4}>
                  {channelData.map((item, index) => (
                    <Cell key={item.label} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                      formatName={tr}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            {channelData.map((item, index) => (
              <div key={item.label} className="sales-product-analytics-dashboard__legend-row">
                <span className="sales-product-analytics-dashboard__legend-dot-item">
                  <span
                    className="sales-product-analytics-dashboard__legend-dot"
                    style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                  />
                  <span>{tr(item.label)}</span>
                </span>
                <span>{localizeDigits(String(item.percentage))}%</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="sales-product-analytics-dashboard__grid sales-product-analytics-dashboard__grid--three">
        <DashboardDataTablePanel
          as="section"
          className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--top-products"
        >
          <DashboardDataTablePanelHeader>
            <div className="sales-product-analytics-dashboard__title-with-tooltip">
              <h3 className="sales-product-analytics-dashboard__card-title">{tr("Top Products")}</h3>
              <DefinitionTooltip label={tr("Top Products")} info={{ description: tr(TOP_PRODUCTS_TOOLTIP.description) }} />
            </div>
          </DashboardDataTablePanelHeader>
          <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
            <DashboardDataTableTable
              className={`${dt.tableCompact} sales-product-analytics-dashboard__table sales-product-analytics-dashboard__table--overview`}
            >
              <thead>
                <tr>
                  <th>{tr("Product")}</th>
                  <th className={dt.number}>{tr("Stock")}</th>
                  <th className={dt.number}>{tr("Price")}</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map(product => (
                  <tr key={product.id}>
                    <td>{isRTL ? product.nameAr : product.name}</td>
                    <td className={dt.number}>
                      <span className="sales-product-analytics-dashboard__pill">
                        {localizeDigits(String(product.totalStock))}
                      </span>
                    </td>
                    <td className={dt.number}>{formatCurrency(product.price)}</td>
                  </tr>
                ))}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>
        </DashboardDataTablePanel>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">
            {tr("Customer Breakdown")}
          </h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={segmentation} dataKey="value" nameKey="label" innerRadius={38} outerRadius={70}>
                  {segmentation.map((item, index) => (
                    <Cell
                      key={item.label}
                      fill={index === 0 ? "#3569db" : "#27c45a"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                      formatName={tr}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            <div className="sales-product-analytics-dashboard__legend-row sales-product-analytics-dashboard__legend-row--center">
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span
                  className="sales-product-analytics-dashboard__legend-dot"
                  style={{ backgroundColor: "#3569db" }}
                />
                <span>{`${tr("New")} (${localizeDigits(String(newCustomers))})`}</span>
              </span>
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span
                  className="sales-product-analytics-dashboard__legend-dot"
                  style={{ backgroundColor: "#27c45a" }}
                />
                <span>{`${tr("Returning")} (${localizeDigits(String(returningCustomers))})`}</span>
              </span>
            </div>
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">{tr("Financial Summary")}</h3>
          <div className="sales-product-analytics-dashboard__summary-list">
            {[
              { label: "Revenue", value: summary.revenue, tone: "neutral" },
              { label: "Gross Profit", value: summary.grossProfit, tone: "positive" },
              { label: "Taxes", value: summary.taxes, tone: "warning" },
              { label: "Expenses", value: summary.expenses, tone: "muted" },
              { label: "Refunds", value: summary.refunds, tone: "negative" },
              { label: "Net Profit", value: summary.netProfit, tone: "positive" },
            ].map(item => (
              <div key={item.label} className="sales-product-analytics-dashboard__summary-row">
                <span>{tr(item.label)}</span>
                <span
                  className={`sales-product-analytics-dashboard__summary-value sales-product-analytics-dashboard__summary-value--${item.tone}`}
                >
                  {formatCurrency(item.value)}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const SalesSection: FC<{
  orders: OmnichannelOrder[];
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ orders, dateRange, onDateRangeChange }) => {
  const { isRTL } = useLanguage();
  const tr = makeTr(isRTL);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<OmnichannelSource | "all">("all");
  const [statusFilter, setStatusFilter] = useState<OmnichannelStatus | "all">("all");
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return orders.filter(order => {
      if (
        query &&
        !order.customerName.toLowerCase().includes(query) &&
        !order.customerNameAr.toLowerCase().includes(query) &&
        !order.orderNumber.toLowerCase().includes(query)
      ) {
        return false;
      }
      if (sourceFilter !== "all" && order.source !== sourceFilter) {
        return false;
      }
      return statusFilter === "all" || order.status === statusFilter;
    });
  }, [orders, search, sourceFilter, statusFilter]);
  useEffect(() => {
    setPage(1);
  }, [search, sourceFilter, statusFilter]);
  const pagedRows = useMemo(() => paginateRows(filtered, page), [filtered, page]);
  const channelData = useMemo(() => getChannelDistribution(orders), [orders]);

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title={tr("Sales Overview")}
        subtitle={tr("Track revenue and order performance across Zid and Odoo channels.")}
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={onDateRangeChange}
          />
        )}
      />
      <OmnichannelExecutiveSummary
        tab="sales"
        dateRange={dateRange}
        orders={orders}
        channelData={channelData}
      />
      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">{tr("Sales by Channel")}</h3>
        <p className="sales-product-analytics-dashboard__card-subtitle">
          {tr("Order revenue split across omnichannel storefronts.")}
        </p>
        <div className="sales-product-analytics-dashboard__chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={channelData} margin={mirrorChartMargin({ left: 0, right: 0, top: 0, bottom: 0 }, isRTL)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
              <XAxis dataKey="label" tick={false} reversed={isRTL} />
              <YAxis
                tick={{ fontSize: 11 }}
                orientation={rtlChartProps(isRTL).yAxisOrientation}
                tickFormatter={value => localizeDigits(`SAR ${Math.round(value / 1000)}k`)}
              />
              <Tooltip
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                    formatLabel={value => tr(value)}
                    hideSeriesName
                  />
                }
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="value" fill="#2c68ad" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <DashboardDataTablePanel
        as="section"
        className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--orders"
      >
        <DashboardDataTablePanelHeader>
          <h3>{tr("Orders")} ({localizeDigits(String(filtered.length))})</h3>
          <div className="sales-product-analytics-dashboard__panel-tools">
            <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
              <Search size={16} />
              <input
                type="search"
                placeholder={tr("Search orders...")}
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--orders"
              value={sourceFilter}
              onChange={value => setSourceFilter(value as OmnichannelSource | "all")}
              options={[
                { value: "all", label: tr("All Sources") },
                { value: "zid", label: "Zid" },
                { value: "odoo", label: "Odoo" },
              ]}
            />
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--orders"
              value={statusFilter}
              onChange={value => setStatusFilter(value as OmnichannelStatus | "all")}
              options={[
                { value: "all", label: tr("All Statuses") },
                { value: "completed", label: tr("Completed") },
                { value: "pending", label: tr("Pending") },
                { value: "refunded", label: tr("Refunded") },
                { value: "cancelled", label: tr("Cancelled") },
              ]}
            />
          </div>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
          <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
            <thead>
              <tr>
                <th>{tr("Order")}</th>
                <th>{tr("Customer")}</th>
                <th>{tr("Source")}</th>
                <th>{tr("Channel")}</th>
                <th className={dt.number}>{tr("Total")}</th>
                <th>{tr("Status")}</th>
                <th>{tr("Date")}</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(order => (
                <tr key={order.id}>
                  <td>{localizeDigits(order.orderNumber)}</td>
                  <td>{isRTL ? order.customerNameAr : order.customerName}</td>
                  <td><SourceBadge source={order.source} /></td>
                  <td>{tr(order.channel === "online" ? "Online Store" : "Physical Store")}</td>
                  <td className={dt.number}>{formatCurrency(order.total)}</td>
                  <td><span className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${order.status}`}>{tr(order.status.charAt(0).toUpperCase() + order.status.slice(1))}</span></td>
                  <td>{localizeDigits(order.date)}</td>
                  </tr>
                ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={page}
          totalPages={tableTotalPages(filtered.length)}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() =>
            setPage(p => Math.min(tableTotalPages(filtered.length), p + 1))
          }
        />
      </DashboardDataTablePanel>
    </div>
  );
};

const InventorySection: FC<{
  products: OmnichannelProduct[];
  dateRange: { startDate: string; endDate: string };
}> = ({ products, dateRange }) => {
  const { isRTL } = useLanguage();
  const tr = makeTr(isRTL);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState<{ key: InventorySortKey; direction: SortDirection } | null>(
    { key: "totalStock", direction: "desc" }
  );
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products.filter(product => {
      if (
        query &&
        !product.name.toLowerCase().includes(query) &&
        !product.nameAr.toLowerCase().includes(query) &&
        !product.sku.toLowerCase().includes(query)
      ) {
        return false;
      }
      return statusFilter === "all" || product.status === statusFilter;
    });
  }, [products, search, statusFilter]);
  const sorted = useMemo(() => {
    if (!sort) {
      return filtered;
    }

    return [...filtered].sort((left, right) => {
      switch (sort.key) {
        case "name":
          return compareDashboardValues(
            isRTL ? left.nameAr : left.name,
            isRTL ? right.nameAr : right.name,
            sort.direction
          );
        case "sku":
          return compareDashboardValues(left.sku, right.sku, sort.direction);
        case "category":
          return compareDashboardValues(
            isRTL ? left.categoryAr : left.category,
            isRTL ? right.categoryAr : right.category,
            sort.direction
          );
        case "zidStock":
          return compareDashboardValues(left.zidStock, right.zidStock, sort.direction);
        case "odooStock":
          return compareDashboardValues(left.odooStock, right.odooStock, sort.direction);
        case "totalStock":
          return compareDashboardValues(left.totalStock, right.totalStock, sort.direction);
        case "reorderPoint":
          return compareDashboardValues(left.reorderPoint, right.reorderPoint, sort.direction);
        case "status":
          return compareDashboardValues(left.status, right.status, sort.direction);
        default:
          return 0;
      }
    });
  }, [filtered, sort, isRTL]);
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sort]);
  const pagedRows = useMemo(() => paginateRows(sorted, page), [sorted, page]);
  const inStock = products.filter(product => product.status === "in_stock").length;
  const lowStock = products.filter(product => product.status === "low_stock").length;
  const outOfStock = products.filter(product => product.status === "out_of_stock").length;
  const inventoryValue = products.reduce((sum, product) => sum + product.totalStock * product.cost, 0);
  const stockComparison = products.slice(0, 10).map(product => {
    const label = isRTL ? product.nameAr : product.name;
    return {
      name: label.length > 16 ? `${label.slice(0, 16)}...` : label,
      Zid: product.zidStock,
      Odoo: product.odooStock,
    };
  });

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title={tr("Inventory Overview")}
        subtitle={tr("Compare stock, availability, and inventory value across both sources.")}
        sticky
      />
      <OmnichannelExecutiveSummary
        tab="inventory"
        dateRange={dateRange}
        products={products}
      />
      <div className="sales-product-analytics-dashboard__metrics">
        <MetricCard
          label="Total Products"
          value={localizeDigits(String(products.length))}
          statusHint="Unified catalog count"
          icon={Package}
          hideTrend
        />
        <MetricCard
          label="In Stock"
          value={localizeDigits(String(inStock))}
          statusHint={
            isRTL
              ? `${Math.round((inStock / products.length) * 100)}% ${tr("available")}`
              : `${Math.round((inStock / products.length) * 100)}% available`
          }
          tone="positive"
          icon={CheckCircle2}
          hideTrend
        />
        <MetricCard
          label="Low Stock"
          value={localizeDigits(String(lowStock))}
          statusHint="Needs reorder attention"
          tone="negative"
          icon={TriangleAlert}
          hideTrend
        />
      </div>

      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">{tr("Stock Comparison")}</h3>
        <p className="sales-product-analytics-dashboard__card-subtitle">
          {tr("Comparative stock visibility across both connected sources.")}
        </p>
        <div className="sales-product-analytics-dashboard__chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stockComparison} layout="vertical" margin={mirrorChartMargin({ left: 0, right: 0, top: 0, bottom: 0 }, isRTL)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
              <XAxis type="number" tick={{ fontSize: 11 }} reversed={isRTL} tickFormatter={value => localizeDigits(String(value))} />
              <YAxis
                dataKey="name"
                type="category"
                width={130}
                tick={{ fontSize: 10 }}
                orientation={rtlChartProps(isRTL).yAxisOrientation}
              />
              <Tooltip
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                    formatName={tr}
                  />
                }
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="Zid" fill={DAILY_LINE_COLORS.zid} radius={rtlChartProps(isRTL).horizontalBarRadius} />
              <Bar dataKey="Odoo" fill={DAILY_LINE_COLORS.odoo} radius={rtlChartProps(isRTL).horizontalBarRadius} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <DashboardDataTablePanel
        as="section"
        className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--inventory"
      >
        <DashboardDataTablePanelHeader>
          <h3>
            {tr("Products")}
          </h3>
          <div className="sales-product-analytics-dashboard__panel-tools">
            <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
              <Search size={16} />
              <input
                type="search"
                placeholder={tr("Search products...")}
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--inventory"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: tr("All status") },
                { value: "in_stock", label: tr("In stock") },
                { value: "low_stock", label: tr("Low stock") },
                { value: "out_of_stock", label: tr("Out of stock") },
              ]}
            />
          </div>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
          <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
            <thead>
              <tr>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "name"))}>
                    {tr("Product")}
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "sku"))}>
                    {tr("SKU")}
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "category"))}>
                    {tr("Category")}
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "zidStock"))}>
                    Zid
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "odooStock"))}>
                    Odoo
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "totalStock"))}>
                    {tr("Total")}
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "reorderPoint"))}>
                    {tr("Reorder")}
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "status"))}>
                    {tr("Status")}
                  </DashboardDataTableSortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(product => (
                <tr key={product.id}>
                  <td>{isRTL ? product.nameAr : product.name}</td>
                  <td>{product.sku}</td>
                  <td>{isRTL ? product.categoryAr : product.category}</td>
                  <td className={dt.number}>{localizeDigits(String(product.zidStock))}</td>
                  <td className={dt.number}>{localizeDigits(String(product.odooStock))}</td>
                  <td className={dt.number}>{localizeDigits(String(product.totalStock))}</td>
                  <td className={dt.number}>{localizeDigits(String(product.reorderPoint))}</td>
                  <td><span className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${product.status}`}>{tr(product.status === "in_stock" ? "In stock" : product.status === "low_stock" ? "Low stock" : "Out of stock")}</span></td>
                </tr>
              ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={page}
          totalPages={tableTotalPages(filtered.length)}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() =>
            setPage(p => Math.min(tableTotalPages(filtered.length), p + 1))
          }
        />
      </DashboardDataTablePanel>
    </div>
  );
};

const CustomersSection: FC<{
  customers: OmnichannelCustomer[];
  previousCustomers: OmnichannelCustomer[];
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ customers, previousCustomers, dateRange, onDateRangeChange }) => {
  const { isRTL } = useLanguage();
  const tr = makeTr(isRTL);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "new" | "returning">("all");
  const [sort, setSort] = useState<{ key: CustomerSortKey; direction: SortDirection } | null>(
    { key: "totalSpent", direction: "desc" }
  );
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return customers.filter(customer => {
      if (typeFilter !== "all" && customer.type !== typeFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      return (
        customer.name.toLowerCase().includes(query) ||
        customer.nameAr.toLowerCase().includes(query) ||
        customer.email.toLowerCase().includes(query)
      );
    });
  }, [customers, search, typeFilter]);
  const sorted = useMemo(() => {
    if (!sort) {
      return filtered;
    }

    return [...filtered].sort((left, right) => {
      switch (sort.key) {
        case "name":
          return compareDashboardValues(
            isRTL ? left.nameAr : left.name,
            isRTL ? right.nameAr : right.name,
            sort.direction
          );
        case "type":
          return compareDashboardValues(left.type, right.type, sort.direction);
        case "totalOrders":
          return compareDashboardValues(left.totalOrders, right.totalOrders, sort.direction);
        case "totalSpent":
          return compareDashboardValues(left.totalSpent, right.totalSpent, sort.direction);
        case "cltv":
          return compareDashboardValues(left.cltv, right.cltv, sort.direction);
        case "lastOrder":
          return compareDashboardValues(left.lastOrder, right.lastOrder, sort.direction);
        default:
          return 0;
      }
    });
  }, [filtered, sort, isRTL]);
  useEffect(() => {
    setPage(1);
  }, [search, sort, typeFilter]);
  const pagedRows = useMemo(() => paginateRows(sorted, page), [sorted, page]);
  const cltvBuckets = useMemo(() => getCltvBuckets(customers), [customers]);
  const segmentation = useMemo(() => getCustomerSegmentation(customers), [customers]);
  const newCustomers = segmentation.find(item => item.label === "New")?.value ?? 0;
  const returningCustomers =
    segmentation.find(item => item.label === "Returning")?.value ?? 0;
  const avgCltv = Math.round(customers.reduce((sum, customer) => sum + customer.cltv, 0) / customers.length);
  const avgOrderValue = Math.round(customers.reduce((sum, customer) => sum + customer.avgOrderValue, 0) / customers.length);

  const previousSegmentation = useMemo(
    () => getCustomerSegmentation(previousCustomers),
    [previousCustomers]
  );
  const previousNewCustomers = previousSegmentation.find(item => item.label === "New")?.value ?? 0;
  const previousAvgCltv = Math.round(
    previousCustomers.reduce((sum, customer) => sum + customer.cltv, 0) / previousCustomers.length
  );
  const previousAvgOrderValue = Math.round(
    previousCustomers.reduce((sum, customer) => sum + customer.avgOrderValue, 0) / previousCustomers.length
  );

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title={tr("Customers Overview")}
        subtitle={tr("Review customer growth, loyalty, and top buyers across channels.")}
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={onDateRangeChange}
          />
        )}
      />
      <OmnichannelExecutiveSummary
        tab="customers"
        dateRange={dateRange}
        customers={customers}
        segmentation={segmentation}
        kpis={{
          totalRevenue: 0,
          totalOrders: 0,
          activeCustomers: customers.length,
          avgOrderValue,
          inventoryValue: 0,
          avgCltv,
        }}
        previousKpis={{
          totalRevenue: 0,
          totalOrders: 0,
          activeCustomers: previousCustomers.length,
          avgOrderValue: previousAvgOrderValue,
          inventoryValue: 0,
          avgCltv: previousAvgCltv,
        }}
      />
      <div className="sales-product-analytics-dashboard__metrics">
        <MetricCard
          label="Total Customers"
          value={localizeDigits(String(customers.length))}
          statusHint="Active Zid and Odoo buyers"
          icon={getDashboardKpiLucideIconComponent("Total Customers")}
          hideTrend
        />
        <MetricCard
          label="New Customers"
          value={localizeDigits(String(segmentation[0].value))}
          changePercent={computeChangePercent(newCustomers, previousNewCustomers)}
          statusHint="Recent acquisitions"
          tone={newCustomers >= previousNewCustomers ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("New Customers")}
        />
        <MetricCard
          label="Avg. CLTV"
          value={formatCurrency(avgCltv)}
          changePercent={computeChangePercent(avgCltv, previousAvgCltv)}
          statusHint="Customer lifetime value"
          tone={avgCltv >= previousAvgCltv ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Avg. CLTV")}
        />
        <MetricCard
          label="Avg. Order Value"
          value={formatCurrency(avgOrderValue)}
          changePercent={computeChangePercent(avgOrderValue, previousAvgOrderValue)}
          statusHint="Average basket size"
          tone={avgOrderValue >= previousAvgOrderValue ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Avg. Order Value")}
        />
      </div>

      <div className="sales-product-analytics-dashboard__grid">
        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">{tr("CLTV Distribution")}</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cltvBuckets} margin={mirrorChartMargin({ left: 0, right: 0, top: 0, bottom: 0 }, isRTL)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
                <XAxis dataKey="label" tick={false} reversed={isRTL} />
                <YAxis tick={{ fontSize: 11 }} orientation={rtlChartProps(isRTL).yAxisOrientation} />
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                      formatLabel={value => localizeDigits(value)}
                      hideSeriesName
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
                <Bar dataKey="value" fill="#2c68ad" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">{tr("Customer Segmentation")}</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={segmentation} dataKey="value" nameKey="label" innerRadius={40} outerRadius={74}>
                  {segmentation.map((item, index) => (
                    <Cell
                      key={item.label}
                      fill={index === 0 ? "#3569db" : "#27c45a"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatNumberTooltipValue(value)}
                      formatName={tr}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            <div className="sales-product-analytics-dashboard__legend-row sales-product-analytics-dashboard__legend-row--center">
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span className="sales-product-analytics-dashboard__legend-dot" style={{ backgroundColor: "#3569db" }} />
                <span>{`${tr("New")} (${localizeDigits(String(newCustomers))})`}</span>
              </span>
              <span className="sales-product-analytics-dashboard__legend-dot-item">
                <span className="sales-product-analytics-dashboard__legend-dot" style={{ backgroundColor: "#27c45a" }} />
                <span>{`${tr("Returning")} (${localizeDigits(String(returningCustomers))})`}</span>
              </span>
            </div>
          </div>
          </section>
      </div>

      <DashboardDataTablePanel
        as="section"
        className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--customers"
      >
        <DashboardDataTablePanelHeader>
          <h3>{tr("Top Customers")}</h3>
          <div className="sales-product-analytics-dashboard__panel-tools">
            <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
              <Search size={16} />
              <input
                type="search"
                placeholder={tr("Search customers...")}
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--orders"
              value={typeFilter}
              onChange={value => setTypeFilter(value as "all" | "new" | "returning")}
              options={[
                { value: "all", label: tr("All types") },
                { value: "new", label: tr("New") },
                { value: "returning", label: tr("Returning") },
              ]}
            />
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--sort"
              value={sort ? `${sort.key}_${sort.direction}` : "totalSpent_desc"}
              onChange={value => setSort(parseKeyDirection<CustomerSortKey>(value))}
              options={OMNICHANNEL_CUSTOMER_SORT_OPTIONS.map(option => ({ ...option, label: tr(option.label) }))}
            />
          </div>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
          <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
            <thead>
              <tr>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "name"))}>
                    {tr("Name")}
                  </DashboardDataTableSortButton>
                </th>
                <th>{tr("Source")}</th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "type"))}>
                    {tr("Type")}
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "totalOrders"))}>
                    {tr("Orders")}
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "totalSpent"))}>
                    {tr("Total Spent")}
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "cltv"))}>
                    {tr("CLTV")}
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton onClick={() => setSort(current => toggleTableSort(current, "lastOrder"))}>
                    {tr("Last Order")}
                  </DashboardDataTableSortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(customer => (
                <tr key={customer.id}>
                  <td>{isRTL ? customer.nameAr : customer.name}</td>
                  <td><SourceBadge source={customer.source} /></td>
                  <td><span className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${customer.type}`}>{tr(customer.type === "new" ? "New" : "Returning")}</span></td>
                  <td className={dt.number}>{localizeDigits(String(customer.totalOrders))}</td>
                  <td className={dt.number}>{formatCurrency(customer.totalSpent)}</td>
                  <td className={dt.number}>{formatCurrency(customer.cltv)}</td>
                  <td>{localizeDigits(customer.lastOrder)}</td>
                </tr>
              ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={page}
          totalPages={tableTotalPages(filtered.length)}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() =>
            setPage(p => Math.min(tableTotalPages(filtered.length), p + 1))
          }
        />
      </DashboardDataTablePanel>
    </div>
  );
};

const FinancialsSection: FC<{
  orders: OmnichannelOrder[];
  previousOrders: OmnichannelOrder[];
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ orders, previousOrders, dateRange, onDateRangeChange }) => {
  const { isRTL } = useLanguage();
  const tr = makeTr(isRTL);
  const summary = useMemo(() => getFinancialSummary(orders), [orders]);
  const previousSummary = useMemo(() => getFinancialSummary(previousOrders), [previousOrders]);
  const monthly = useMemo(() => getMonthlyFinancials(orders), [orders]);
  const channelData = useMemo(() => getChannelDistribution(orders), [orders]);
  const taxBreakdown = useMemo(() => getTaxBreakdown(summary.taxes), [summary.taxes]);
  const margin = summary.revenue
    ? Math.round((summary.netProfit / summary.revenue) * 100)
    : 0;

  return (
    <div className="sales-product-analytics-dashboard__section">
      <DashboardSectionHeader
        title={tr("Financials Overview")}
        subtitle={tr("Analyze omnichannel revenue, profit, taxes, and refund performance.")}
        sticky
        actions={(
          <OrganicDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={onDateRangeChange}
          />
        )}
      />
      <OmnichannelExecutiveSummary
        tab="financials"
        dateRange={dateRange}
        summary={summary}
        previousSummary={previousSummary}
        monthly={monthly}
        channelData={channelData}
        taxBreakdown={taxBreakdown}
      />
      <div className="sales-product-analytics-dashboard__metrics">
        <MetricCard
          label="Revenue"
          value={formatCurrency(summary.revenue)}
          changePercent={computeChangePercent(summary.revenue, previousSummary.revenue)}
          tone={summary.revenue >= previousSummary.revenue ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Revenue")}
        />
        <MetricCard
          label="Net Profit"
          value={formatCurrency(summary.netProfit)}
          changePercent={computeChangePercent(summary.netProfit, previousSummary.netProfit)}
          statusHint={
            isRTL
              ? `${margin}% ${tr("margin")}`
              : `${margin}% margin`
          }
          tone={summary.netProfit >= previousSummary.netProfit ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Net Profit")}
        />
        <MetricCard
          label="Total Taxes"
          value={formatCurrency(summary.taxes)}
          changePercent={computeChangePercent(summary.taxes, previousSummary.taxes)}
          tone="neutral"
          icon={getDashboardKpiLucideIconComponent("Total Taxes")}
        />
        <MetricCard
          label="Refunds"
          value={formatCurrency(summary.refunds)}
          changePercent={computeChangePercent(summary.refunds, previousSummary.refunds)}
          tone={summary.refunds <= previousSummary.refunds ? "positive" : "negative"}
          icon={getDashboardKpiLucideIconComponent("Refunds")}
        />
      </div>

      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">{tr("Monthly P&L")}</h3>
        <div className="sales-product-analytics-dashboard__chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={mirrorChartMargin({ left: 0, right: 0, top: 0, bottom: 0 }, isRTL)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10 }}
                reversed={isRTL}
                tickFormatter={value => localizeDigits(formatMonthLabel(value))}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                orientation={rtlChartProps(isRTL).yAxisOrientation}
                tickFormatter={value => localizeDigits(`SAR ${Math.round(value / 1000)}k`)}
              />
              <Tooltip
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                    formatName={tr}
                    formatLabel={value => localizeDigits(formatMonthLabel(value))}
                  />
                }
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="revenue" name="Revenue" fill="#3569db" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#e0534d" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#2dbb5f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="sales-product-analytics-dashboard__chart-legend">
          <span className="sales-product-analytics-dashboard__chart-legend-item sales-product-analytics-dashboard__chart-legend-item--revenue">
            <span className="sales-product-analytics-dashboard__chart-legend-swatch" />
            <span>{tr("Revenue")}</span>
          </span>
          <span className="sales-product-analytics-dashboard__chart-legend-item sales-product-analytics-dashboard__chart-legend-item--expenses">
            <span className="sales-product-analytics-dashboard__chart-legend-swatch" />
            <span>{tr("Expenses")}</span>
          </span>
          <span className="sales-product-analytics-dashboard__chart-legend-item sales-product-analytics-dashboard__chart-legend-item--profit">
            <span className="sales-product-analytics-dashboard__chart-legend-swatch" />
            <span>{tr("Profit")}</span>
          </span>
        </div>
      </section>

      <div className="sales-product-analytics-dashboard__grid">
        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">{tr("Revenue by Channel")}</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={channelData} dataKey="value" nameKey="label" innerRadius={42} outerRadius={76}>
                  {channelData.map((item, index) => (
                    <Cell
                  key={item.label}
                  fill={index === 0 ? "#3569db" : "#27c45a"}
                />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                      formatName={tr}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            {channelData.map((item, index) => (
              <div key={item.label} className="sales-product-analytics-dashboard__legend-row">
                <span className="sales-product-analytics-dashboard__legend-dot-item">
                  <span
                    className="sales-product-analytics-dashboard__legend-dot"
                    style={{
                      backgroundColor: index === 0 ? "#3569db" : "#27c45a",
                    }}
                  />
                  <span>{tr(item.label)}</span>
                </span>
                <span>{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="sales-product-analytics-dashboard__card">
          <h3 className="sales-product-analytics-dashboard__card-title">{tr("Tax Breakdown")}</h3>
          <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--sm">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={taxBreakdown} dataKey="value" nameKey="label" innerRadius={42} outerRadius={76}>
                  {taxBreakdown.map((item, index) => (
                    <Cell
                      key={item.label}
                      fill={index === 0 ? "#f8a208" : index === 1 ? "#9b5785" : "#9ca8bd"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomChartTooltip
                      formatTooltip={(value: number) => formatCurrencyTooltipValue(value)}
                      formatName={tr}
                    />
                  }
                  wrapperStyle={{ pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sales-product-analytics-dashboard__legend-list">
            {taxBreakdown.map(item => (
              <div key={item.label} className="sales-product-analytics-dashboard__legend-row">
                <span className="sales-product-analytics-dashboard__legend-dot-item">
                  <span
                    className="sales-product-analytics-dashboard__legend-dot"
                    style={{
                      backgroundColor:
                        item.label === "Sales Tax"
                          ? "#f8a208"
                          : item.label === "VAT"
                            ? "#9b5785"
                            : "#9ca8bd",
                    }}
                  />
                  <span>{tr(item.label)}</span>
                </span>
                <span>{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="sales-product-analytics-dashboard__card">
        <h3 className="sales-product-analytics-dashboard__card-title">{tr("Financial Breakdown")}</h3>
        <div className="sales-product-analytics-dashboard__financial-bars">
            {[
              { label: "Gross Revenue", value: summary.revenue, ratio: 100 },
              { label: "Cost of Goods Sold", value: -summary.cogs, ratio: (summary.cogs / Math.max(summary.revenue, 1)) * 100 },
              { label: "Gross Profit", value: summary.grossProfit, ratio: (summary.grossProfit / Math.max(summary.revenue, 1)) * 100 },
              { label: "Operating Expenses", value: -summary.expenses, ratio: (summary.expenses / Math.max(summary.revenue, 1)) * 100 },
              { label: "Taxes", value: -summary.taxes, ratio: (summary.taxes / Math.max(summary.revenue, 1)) * 100 },
              { label: "Refunds", value: -summary.refunds, ratio: (summary.refunds / Math.max(summary.revenue, 1)) * 100 },
              { label: "Net Profit", value: summary.netProfit, ratio: (Math.abs(summary.netProfit) / Math.max(summary.revenue, 1)) * 100 },
            ].map(item => (
            <div key={item.label}>
              <div className="sales-product-analytics-dashboard__financial-bar-copy">
                <span>{tr(item.label)}</span>
                <span>{item.value < 0 ? "-" : ""}{formatCurrency(Math.abs(item.value))}</span>
              </div>
              <div className="sales-product-analytics-dashboard__financial-bar-track">
                <div
                  className={`sales-product-analytics-dashboard__financial-bar-fill ${item.value < 0 ? "sales-product-analytics-dashboard__financial-bar-fill--negative" : ""}`}
                  style={{ width: `${Math.min(Math.abs(item.ratio), 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

const OmnichannelAnalyticsTabsDashboard: FC = () => {
  const { isRTL } = useLanguage();
  const tr = makeTr(isRTL);
  isArabicRender = isRTL;
  const [activeTab, setActiveTab] = useState<OmnichannelTabId>("overview");
  const [dateRange, setDateRange] = useState(() => {
    const end = dayjs();
    const start = end.subtract(29, "day");
    return {
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
    };
  });
  const orders = useMemo(() => generateOmnichannelOrders(dateRange), [dateRange]);
  const products = useMemo(() => generateOmnichannelProducts(), []);
  const customers = useMemo(() => generateOmnichannelCustomers(dateRange), [dateRange]);
  const previousDateRange = useMemo(() => getPreviousDateRange(dateRange), [dateRange]);
  const previousOrders = useMemo(
    () => generateOmnichannelOrders(previousDateRange),
    [previousDateRange]
  );
  const previousCustomers = useMemo(
    () => generateOmnichannelCustomers(previousDateRange),
    [previousDateRange]
  );

  return (
    <div className="sales-product-analytics-dashboard">
      <div className="sales-product-analytics-dashboard__tabs">
        {OMNICHANNEL_TAB_ITEMS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`sales-product-analytics-dashboard__tab ${activeTab === tab.id ? "sales-product-analytics-dashboard__tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tr(tab.label)}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? (
        <OverviewSection
          orders={orders}
          customers={customers}
          products={products}
          previousOrders={previousOrders}
          previousCustomers={previousCustomers}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      ) : null}
      {activeTab === "sales" ? (
        <SalesSection
          orders={orders}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      ) : null}
      {activeTab === "inventory" ? (
        <InventorySection
          products={products}
          dateRange={dateRange}
        />
      ) : null}
      {activeTab === "customers" ? (
        <CustomersSection
          customers={customers}
          previousCustomers={previousCustomers}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      ) : null}
      {activeTab === "financials" ? (
        <FinancialsSection
          orders={orders}
          previousOrders={previousOrders}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      ) : null}
    </div>
  );
};

export default OmnichannelAnalyticsTabsDashboard;
